import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Scale, Clock, CheckCircle, XCircle, Send, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const REJECTION_REASON_LABELS = {
  too_confusing: 'Too Confusing / Hard to Understand',
  missing_context: 'Missing Context',
  unclear_problem: 'Unclear Problem Statement',
  no_impact_evidence: 'No Impact Evidence',
  duplicate_issue: 'Duplicate Issue',
  not_actionable: 'Not Actionable',
  insufficient_detail: 'Insufficient Detail',
};

export default function IssueAppealPanel({ issue, officers, open, onClose }) {
  const queryClient = useQueryClient();
  const [appealReason, setAppealReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });

  const isReporter = user?.email === issue?.reported_by;
  const isDeadlinePassed = issue?.appeal_deadline && new Date(issue.appeal_deadline) < new Date();
  const canAppeal = isReporter && issue?.appeal_status === 'none' && !isDeadlinePassed;
  const canReview = !isReporter && issue?.appeal_status === 'appealed';

  const reporter = officers?.find(o => o.user_email === issue?.reported_by);

  const submitAppealMutation = useMutation({
    mutationFn: async () => {
      const appealEntry = {
        appealed_by: user?.email,
        appealed_at: new Date().toISOString(),
        reason: appealReason,
        reviewed_by: null,
        reviewed_at: null,
        outcome: null,
        notes: null
      };

      await base44.entities.Issue.update(issue.id, {
        appeal_status: 'appealed',
        appeal_reason: appealReason,
        appealed_by: user?.email,
        appealed_at: new Date().toISOString(),
        appeal_history: [...(issue.appeal_history || []), appealEntry]
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setAppealReason('');
      onClose();
    }
  });

  const reviewAppealMutation = useMutation({
    mutationFn: async (outcome) => {
      const history = [...(issue.appeal_history || [])];
      if (history.length > 0) {
        history[history.length - 1] = {
          ...history[history.length - 1],
          reviewed_by: user?.email,
          reviewed_at: new Date().toISOString(),
          outcome,
          notes: reviewNotes
        };
      }

      const updateData = {
        appeal_status: outcome === 'accepted' ? 'appeal_accepted' : 'appeal_denied',
        appeal_reviewed_by: user?.email,
        appeal_reviewed_at: new Date().toISOString(),
        appeal_review_notes: reviewNotes,
        appeal_history: history
      };

      if (outcome === 'accepted') {
        updateData.status = 'open';
        updateData.classification_gate1 = 'pending';
        updateData.rejection_reason = null;
        updateData.rejection_notes = null;
        updateData.rejected_by = null;
        updateData.rejected_at = null;
        updateData.re_review_flagged = true;
        updateData.re_review_reason = `Appeal accepted: ${reviewNotes || issue.appeal_reason}`;
      }

      await base44.entities.Issue.update(issue.id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setReviewNotes('');
      onClose();
    }
  });

  if (!issue) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="hc-issueappealpanel-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-400" />
            {canReview ? 'Review Appeal' : 'Issue Appeal'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Issue Context */}
          <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <p className="text-sm font-medium text-slate-200">{issue.title}</p>
            {issue.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{issue.description}</p>}
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-600">
                By: {reporter?.callsign || issue.reported_by}
              </Badge>
              {issue.status === 'rejected' && (
                <Badge className="bg-red-500/20 text-red-300 text-[10px]">Rejected</Badge>
              )}
              {issue.status === 'decision_made' && (
                <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px]">Decision Made</Badge>
              )}
            </div>
          </div>

          {/* Rejection Info */}
          {issue.rejection_reason && (
            <div className="p-3 rounded-lg bg-red-950/20 border border-red-500/20">
              <p className="text-xs font-semibold text-red-300 mb-1">Rejection Reason</p>
              <p className="text-xs text-red-200">{REJECTION_REASON_LABELS[issue.rejection_reason] || issue.rejection_reason}</p>
              {issue.rejection_notes && (
                <p className="text-xs text-red-300/70 mt-1 italic">{issue.rejection_notes}</p>
              )}
              {issue.rejection_corrective_action && (
                <div className="mt-2 p-2 rounded bg-amber-950/30 border border-amber-500/20">
                  <p className="text-[10px] text-amber-400 font-semibold mb-0.5">Corrective Action Required</p>
                  <p className="text-xs text-amber-200">{issue.rejection_corrective_action}</p>
                </div>
              )}
            </div>
          )}

          {/* Appeal Deadline */}
          {issue.appeal_deadline && issue.appeal_status === 'none' && (
            <div className="flex items-center gap-2 text-xs">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span className={isDeadlinePassed ? 'text-red-400' : 'text-amber-300'}>
                Appeal deadline: {format(new Date(issue.appeal_deadline), 'MMM d, yyyy')}
                {isDeadlinePassed && ' (expired)'}
              </span>
            </div>
          )}

          {/* Existing Appeal Info */}
          {issue.appeal_status === 'appealed' && (
            <div className="p-3 rounded-lg bg-amber-950/20 border border-amber-500/20">
              <p className="text-xs font-semibold text-amber-300 mb-1">Appeal Filed</p>
              <p className="text-xs text-amber-200">{issue.appeal_reason}</p>
              <p className="text-[10px] text-amber-400/70 mt-1">
                By {officers?.find(o => o.user_email === issue.appealed_by)?.callsign || issue.appealed_by}
                {issue.appealed_at && ` on ${format(new Date(issue.appealed_at), 'MMM d, yyyy')}`}
              </p>
            </div>
          )}

          {/* Appeal Result */}
          {(issue.appeal_status === 'appeal_accepted' || issue.appeal_status === 'appeal_denied') && (
            <div className={`p-3 rounded-lg border ${issue.appeal_status === 'appeal_accepted' ? 'bg-emerald-950/20 border-emerald-500/20' : 'bg-red-950/20 border-red-500/20'}`}>
              <div className="flex items-center gap-2 mb-1">
                {issue.appeal_status === 'appeal_accepted' 
                  ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                  : <XCircle className="w-4 h-4 text-red-400" />
                }
                <p className={`text-xs font-semibold ${issue.appeal_status === 'appeal_accepted' ? 'text-emerald-300' : 'text-red-300'}`}>
                  Appeal {issue.appeal_status === 'appeal_accepted' ? 'Accepted' : 'Denied'}
                </p>
              </div>
              {issue.appeal_review_notes && (
                <p className="text-xs text-slate-300 mt-1">{issue.appeal_review_notes}</p>
              )}
            </div>
          )}

          {/* Submit Appeal Form */}
          {canAppeal && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-300 font-semibold">New Evidence / Context *</Label>
                <Textarea data-testid="hc-issueappealpanel-textarea"
                  value={appealReason}
                  onChange={(e) => setAppealReason(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200 text-sm"
                  rows={4}
                  placeholder="Provide new evidence, additional context, or explain why the rejection was incorrect..."
                />
              </div>
              <Button
                disabled={!appealReason.trim() || submitAppealMutation.isPending}
                onClick={() => submitAppealMutation.mutate()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white h-9 text-sm"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {submitAppealMutation.isPending ? 'Submitting...' : 'Submit Appeal'}
              </Button>
            </div>
          )}

          {/* Review Appeal Form (for council members) */}
          {canReview && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-300 font-semibold">Review Notes</Label>
                <Textarea data-testid="hc-issueappealpanel-textarea-1"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-200 text-sm"
                  rows={3}
                  placeholder="Notes on your decision..."
                />
              </div>
              <div className="flex gap-2">
                <Button
                  disabled={reviewAppealMutation.isPending}
                  onClick={() => reviewAppealMutation.mutate('accepted')}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm"
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  Accept — Return to Queue
                </Button>
                <Button
                  disabled={reviewAppealMutation.isPending}
                  onClick={() => reviewAppealMutation.mutate('denied')}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white h-9 text-sm"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Deny Appeal
                </Button>
              </div>
            </div>
          )}

          {/* Appeal History */}
          {(issue.appeal_history || []).length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Appeal History</p>
              {issue.appeal_history.map((entry, idx) => (
                <div key={idx} className="p-2 rounded bg-slate-800/40 text-xs space-y-1">
                  <p className="text-slate-300">
                    <span className="text-slate-500">Filed by</span> {officers?.find(o => o.user_email === entry.appealed_by)?.callsign || entry.appealed_by}
                    {entry.appealed_at && <span className="text-slate-500"> on {format(new Date(entry.appealed_at), 'MMM d')}</span>}
                  </p>
                  <p className="text-slate-400 italic">{entry.reason}</p>
                  {entry.outcome && (
                    <p className={entry.outcome === 'accepted' ? 'text-emerald-400' : 'text-red-400'}>
                      {entry.outcome === 'accepted' ? '✓ Accepted' : '✗ Denied'}
                      {entry.reviewed_by && ` by ${officers?.find(o => o.user_email === entry.reviewed_by)?.callsign || entry.reviewed_by}`}
                      {entry.notes && ` — ${entry.notes}`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}